import {AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip
} from 'chart.js';
import {CurrencyExchange} from '@app/shared';

// Registrar elementos necesarios de Chart.js
Chart.register(LinearScale, TimeScale, PointElement, LineElement, Filler, Tooltip, LineController, CategoryScale);

@Component({
  selector: 'app-rate-dot-chart',
  standalone: false,

  templateUrl: './rate-dot-chart.component.html',
  styleUrl: './rate-dot-chart.component.less'
})
export class RateDotChartComponent implements AfterViewInit, OnChanges{

  @ViewChild('chartCanvas') private chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() data: CurrencyExchange[] = [];

  private chart: Chart<'line'> | null = null; // Tipo específico para gráfico de línea
  private readonly MAX_ITEMS = 10;

  ngAfterViewInit() {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.chart) {
      this.updateChart();
    }
  }

  private prepareChartData(): { x: Date; y: number }[] {
    // Ordenar datos por fecha (más recientes primero)
    const sortedData = [...this.data]
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
      .slice(0, this.MAX_ITEMS);

    const chartData = sortedData.map(d => ({
      x: new Date(d.startDate),
      y: d.baseRate
    }));

    // Rellenar con ceros si faltan elementos
    const missingItems = this.MAX_ITEMS - chartData.length;
    if (missingItems > 0) {
      const lastDate = chartData.length > 0
        ? new Date(chartData[chartData.length - 1].x)
        : new Date();

      for (let i = 0; i < missingItems; i++) {
        const newDate = new Date(lastDate);
        newDate.setDate(newDate.getDate() - (i + 1));
        chartData.push({ x: newDate, y: 0 });
      }
    }

    // Ordenar final para el gráfico (más antiguo primero)
    return chartData.sort((a, b) => a.x.getTime() - b.x.getTime());
  }

  private createChart() {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    // Crear gradiente
    const gradient = ctx.createLinearGradient(0, 0, 0, this.chartCanvas.nativeElement.height);
    gradient.addColorStop(0, 'rgba(137, 121, 255, 0.4)');   // semi-transparente
    gradient.addColorStop(1, 'rgba(137, 121, 255, 0.05)');  // Más transparente

    const chartData = this.prepareChartData();

    // Solución al error de tipos: Especificar tipo de dataset y usar "as any" en opciones complejas
    const dataset: any = {
      label: 'Base Rate',
      data: chartData,
      backgroundColor: gradient,
      borderColor: '#8E4EC6',
      borderWidth: 0.5,
      fill: true,
      tension: 0.4,
      // Configuración de puntos:
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#8E4EC6',
      pointBorderColor: 'white',
      pointBorderWidth: 1,
      pointStyle: 'circle'
    };

    const maxValue = this.getMaxValue();
    const stepSize = this.getStepSize(maxValue)

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: [dataset]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'category', // Cambiar de 'time' a 'category'
            labels: this.getDateLabels(), // Agregar función para generar etiquetas
            grid: {
              display: false
            },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              autoSkip: false // Mostrar todas las etiquetas
            }
          },
          y: {
            min: 0,
            max: maxValue,
            ticks: {
              stepSize: stepSize,
              callback: (value: any) => `${parseFloat(value.toFixed(4))}`
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            },
            border: {
              dash: [4, 4]
            }
          }
        },
        plugins: {
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: (items: any) => {
                const date = new Date(items[0].raw.x);
                return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} - ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
              },
              label: (context: any) => `Tasa: ${context.parsed.y}`
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        }
      } as any // Solución para opciones complejas
    });
  }

  private getDateLabels(): string[] {
    return this.prepareChartData().map(item => {
      const date = item.x;
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });
  }

  private updateChart() {
    if (!this.chart) return;

    this.chart.data.datasets[0].data = this.prepareChartData() as any;
    this.chart.update();
  }

  getMaxValue(): number {
    let maxValue = 0;
    this.data.forEach(i => i.baseRate > maxValue ? maxValue = i.baseRate : null);
    const roundedStep = parseFloat( (1.25 * maxValue).toFixed(4));
    return roundedStep || 10;
  }

  getStepSize(maxValue: number): number {
    const rawStep = maxValue / 5;

    // Redondear a máximo 4 decimales
    const roundedStep = parseFloat(rawStep.toFixed(4));

    // Asegurar que no sea cero
    return roundedStep || 1;
  }
}
